import React, {
    ReactElement,
    useState,
} from "react";
import { Dropdown } from "react-bootstrap";
import { BiCheckSquare, BiSquareRounded } from "react-icons/bi";
import { FaSlack } from "react-icons/fa";
import { GrFormFilter } from "react-icons/gr";
import { PiMicrosoftTeamsLogo } from "react-icons/pi";
import {
    useGetMicrosoftTeamsChannelSettingsQuery,
} from "../../../../../__generated__/api";
import { Loading } from "../../../../../components";
import useArticleContext from "../../../../../contexts/HelpContext";
import useRouting, { Link } from "../../../../../contexts/Routing";
import { ErrorRemoteView } from "../../../../error/Error";
import { SettingsProps } from "../../base";
import { Filters } from "../../../../../components/filters/Filters";

export default function MicrosoftTeamsSettings(props: SettingsProps): ReactElement {
    useArticleContext("urn:fq:article:8769af37db0c44c887cc527d2a885100");

    const { Paths } = useRouting();
    const { data: chatSettings, loading: isChatLoading, error: chatError } = useGetMicrosoftTeamsChannelSettingsQuery({
        fetchPolicy: "no-cache",
    });

    const [search, setSearch] = useState("");
    const [tags, setTags] = useState<Set<string>>(new Set());

    let channels = chatSettings?.chatSettingsMany?.sortBy(
        row => row.options?.microsoftTeams?.workspaceName,
        row => row.options?.microsoftTeams?.name?.toLowerCase() ?? "",
    ) ?? [];

    if (tags.size > 0) {
        channels = channels.filter(channel => channel.options?.microsoftTeams?.tags?.some(tag => tags.has(tag)));
    }

    if (search) {
        const searchText = search.toLowerCase();
        channels = channels.filter(channel => channel.options?.microsoftTeams?.name?.toLowerCase().includes(searchText)
             || channel.options?.microsoftTeams?.workspaceName?.toLowerCase().includes(searchText)
             || channel.options?.microsoftTeams?.tags?.some(tag => tag.toLowerCase().includes(searchText)));
    }

    const tagList = (chatSettings?.chatSettingsMany?.flatMap(setting => setting.options?.microsoftTeams?.tags) ?? [])
        .dedupe()
        .filterNull()
        .map(row => ({
            value: row,
            label: row,
        }));

    return (
        <form className="flex-grow-1">

            <div className="border p-3 rounded mb-4 bg-white">
                <h5 className="mb-3 text-light">
                    Teams
                </h5>
                {isChatLoading && <Loading />}
                {chatError && <ErrorRemoteView error={chatError} />}
                {chatSettings?.chatSettingsMany?.isEmpty && (
                    <div className="text-light text-center my-4">
                        Agent is not installed in any channels in Microsoft Teams. Please install
                        Agent in a team to continue.
                    </div>
                )}
                {chatSettings?.chatSettingsMany?.isNotEmpty && (
                    <div>
                        <div className="d-flex mb-1">
                            <Filters
                                categories={tagList}
                                selectedCategories={new Set(tags)}
                                search={search}
                                onSetSearch={setSearch}
                                onSelected={selected => setTags(selected)}
                            />
                        </div>
                        {channels.isEmpty && (search || tags.size > 0) && (
                            <div className="text-light text-center">
                                No Channels found matching your filters
                            </div>
                        )}
                        {channels.isNotEmpty && (
                            <div className="list-group">

                                {channels?.map(setting => (
                                    <Link
                                        key={setting.id}
                                        to={Paths.ADMINISTRATOR_SETTING_ID}
                                        params={{
                                            id: setting.id,
                                            setting: "chat",
                                        }}
                                        className={"list-group-item list-group-item-action d-flex "
                                                + "align-items-center justify-content-between"}
                                    >
                                        <div className="d-flex align-items-center">

                                            <PiMicrosoftTeamsLogo />

                                            <h5 className="mb-0 d-inline ms-2">
                                                {setting.options?.microsoftTeams?.workspaceName && (
                                                    <small className="me-2 badge bg-light text-white">
                                                        {setting.options?.microsoftTeams?.workspaceName}
                                                    </small>
                                                )}
                                                {setting.options?.microsoftTeams?.name}
                                            </h5>

                                        </div>

                                        <small className="ms-3 text-light">
                                            {setting.options?.microsoftTeams?.tags?.dedupe().map(tag => (
                                                <span
                                                    key={`${setting.id}:${tag}`}
                                                    className="me-2 badge rounded-pill bg-blue-dark"
                                                >
                                                    #
                                                    {tag.toLowerCase()}
                                                </span>
                                            ))}
                                        </small>
                                    </Link>

                                ))}
                            </div>
                        )}

                    </div>
                )}

            </div>
        </form>
    );
}
